import DatabaseManager from './DatabaseManager';

const {
  BandaMensual,
  BandaTrimestral,
  Parametro,
  Presupuesto,
  Venta,
  CalculoComision,
  Ejecutivo,
  Ciudad,
  Medio
} = DatabaseManager;

class CommissionCalculator {
  constructor() {
    this.cache = new Map();
  }

  // Obtener parámetros del sistema con prioridad (Tipo -> Ciudad/Perfil -> Global)
  async getParametro(nombre, ciudadId = null, perfilVenta = null, tipoPresupuestoId = null) {
    const cacheKey = `param_${nombre}_${ciudadId}_${perfilVenta}_${tipoPresupuestoId}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    let ciudad = null;
    if (ciudadId) {
      ciudad = await Ciudad.findByPk(ciudadId);
    }

    // Ordenamos por especificidad
    const params = await Parametro.findAll({ 
      where: { nombre },
      order: [
        ['tipoPresupuestoId', 'DESC'],
        ['grupoCiudadId', 'DESC'], 
        ['perfilVenta', 'DESC'],
      ]
    });

    if (params.length === 0) return 0;

    let parametro = null;
    // Buscar la configuración más específica que empalme
    for(const p of params) {
        if((p.tipoPresupuestoId === null || p.tipoPresupuestoId === tipoPresupuestoId) &&
           (p.grupoCiudadId === null || (ciudad && p.grupoCiudadId === ciudad.grupoCiudadId)) &&
           (p.perfilVenta === null || p.perfilVenta === perfilVenta)) {
            parametro = p;
            break;
        }
    }

    const valor = parametro ? parseFloat(parametro.valor) : parseFloat(params[params.length-1].valor);
    this.cache.set(cacheKey, valor);
    return valor;
  }

  // Obtener banda según cumplimiento, tipo (mensual/trim), ciudad, perfil y tipo presupuesto (Base/Adicional)
  async getBanda(cumplimiento, tipoBanda, ciudadId = null, perfilVenta = null, tipoPresupuestoId = null) {
    const cacheKey = `banda_${tipoBanda}_${cumplimiento}_${ciudadId}_${perfilVenta}_${tipoPresupuestoId}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    let ciudad = null;
    if (ciudadId) {
      ciudad = await Ciudad.findByPk(ciudadId);
    }

    const Model = tipoBanda === 'mensual' ? BandaMensual : BandaTrimestral;
    
    // Buscar todas las bandas válidas para el nivel de cumplimiento
    const bandas = await Model.findAll({
      where: {
        activo: true,
        desde: { $lte: cumplimiento },
        hasta: { $gte: cumplimiento }
      },
      order: [
        ['tipoPresupuestoId', 'DESC'],
        ['grupoCiudadId', 'DESC'],
        ['perfilVenta', 'DESC']
      ]
    });

    if (bandas.length === 0) return null;

    let banda = null;
    for(const b of bandas) {
        if((b.tipoPresupuestoId === null || b.tipoPresupuestoId === tipoPresupuestoId) &&
           (b.grupoCiudadId === null || (ciudad && b.grupoCiudadId === ciudad.grupoCiudadId)) &&
           (b.perfilVenta === null || b.perfilVenta === perfilVenta)) {
            banda = b;
            break;
        }
    }

    const result = banda || bandas[bandas.length - 1];
    this.cache.set(cacheKey, result);
    return result;
  }

  // Calcular factor balance trimestral
  async getFactorBalance(ejecutivoId, anio, trimestre) {
    const ejecutivo = await Ejecutivo.findByPk(ejecutivoId);
    if(!ejecutivo) return 0;
    
    const ventas = await Venta.findAll({
      where: { ejecutivoId, anio, trimestre, activo: true },
      include: [{ model: Medio }]
    });

    const umbralMinimo = await this.getParametro('umbral_minimo_medio', ejecutivo.ciudadId, ejecutivo.perfilVenta);
    // Recuperar configuración de la DB 
    const cb_condicion_0 = await this.getParametro('balance_condicion_min_0.7', ejecutivo.ciudadId, ejecutivo.perfilVenta) || 0;
    const cb_condicion_1 = await this.getParametro('balance_condicion_min_0.8', ejecutivo.ciudadId, ejecutivo.perfilVenta) || 0.7;
    const cb_condicion_2 = await this.getParametro('balance_condicion_todos_0.85', ejecutivo.ciudadId, ejecutivo.perfilVenta) || 1;
    const cb_condicion_3 = await this.getParametro('balance_condicion_todos_0.95', ejecutivo.ciudadId, ejecutivo.perfilVenta) || 1.1;
    const cb_condicion_4 = await this.getParametro('balance_condicion_todos_1.00', ejecutivo.ciudadId, ejecutivo.perfilVenta) || 1.25;

    let mediosBajo70 = 0;

    const cumplimientoMedios = ventas.map(venta => ({
      medio: venta.Medio.nombre,
      cumplimiento: venta.ventaReal / venta.presupuesto
    }));

    cumplimientoMedios.forEach(medio => {
      if (medio.cumplimiento < umbralMinimo) {
        mediosBajo70++;
      }
    });

    // Determinar factor balance según reglas
    const numMediosBajoBloquean = await this.getParametro('medios_bajo_70', ejecutivo.ciudadId, ejecutivo.perfilVenta) || 1;

    if (cumplimientoMedios.some(m => m.cumplimiento < 0.7)) {
      return cb_condicion_0; 
    } else if (mediosBajo70 >= numMediosBajoBloquean) {
      return 0; // Bloqueo por cantidad de medios bajo umbral
    } else if (cumplimientoMedios.every(m => m.cumplimiento >= 1)) {
      return cb_condicion_4; 
    } else if (cumplimientoMedios.every(m => m.cumplimiento >= 0.95)) {
      return cb_condicion_3; 
    } else if (cumplimientoMedios.every(m => m.cumplimiento >= 0.85)) {
      return cb_condicion_2; 
    } else if (cumplimientoMedios.some(m => m.cumplimiento < 0.8)) {
      return cb_condicion_1; 
    } else {
      return cb_condicion_2; 
    }
  }

  // Calcular cumplimiento integral
  async calcularCumplimientoIntegral(ejecutivoId, anio, mes, tipo) {
    const ventas = await Venta.findAll({
      where: {
        ejecutivoId,
        anio,
        ...(tipo === 'mensual' ? { mes } : { trimestre: Math.ceil(mes / 3) }),
        activo: true
      },
      include: [{ model: Medio }]
    });

    if (ventas.length === 0) return 0;

    let sumaPonderada = 0;
    let sumaPesos = 0;

    ventas.forEach(venta => {
      const cumplimiento = venta.ventaReal / venta.presupuesto;
      const peso = venta.Medio.pesoEstrategico;
      
      sumaPonderada += cumplimiento * peso;
      sumaPesos += peso;
    });

    return sumaPesos > 0 ? sumaPonderada / sumaPesos : 0;
  }

  // Calcular comisión mensual
  async calcularComisionMensual(ejecutivoId, ciudadId, medioId, anio, mes) {
    try {
      // Sumar todos los presupuestos activos para este periodo
      const presupuestos = await Presupuesto.findAll({
        where: {
          ejecutivoId,
          ciudadId,
          medioId,
          anio,
          mes,
          activo: true
        }
      });

      // Group budgets by TipoPresupuestoId
      const pptoByType = {};
      const budgetTypes = await DatabaseManager.TipoPresupuesto.findAll();
      
      presupuestos.forEach(p => {
        // Resolve string type to actual TipoPresupuesto ID
        let typeId = p.tipoPresupuestoId;
        if (!typeId && p.tipo) {
          const matched = budgetTypes.find(bt => bt.nombre.toLowerCase() === p.tipo.toLowerCase());
          if (matched) typeId = matched.id;
        }
        // Default to 'Base' ID if not found
        if (!typeId) {
          const baseType = budgetTypes.find(bt => bt.nombre === 'Base');
          typeId = baseType ? baseType.id : null;
        }

        pptoByType[typeId] = (pptoByType[typeId] || 0) + (p.presupuesto || 0);
      });

      const venta = await Venta.findOne({
        where: { ejecutivoId, ciudadId, medioId, anio, mes, activo: true }
      });
      const ejecutivo = await Ejecutivo.findByPk(ejecutivoId);

      if (!venta) {
        return {
          success: false,
          message: 'No se encontraron ventas registradas para este período',
          data: null
        };
      }

      const ventaRealTotal = venta.ventaReal || 0;
      const totalPpto = Object.values(pptoByType).reduce((a, b) => a + b, 0);
      
      // Calculate integral compliance (global for all types)
      const cumplimientoIntegral = await this.calcularCumplimientoIntegral(ejecutivoId, anio, mes, 'mensual');
      const compuertaIntegral = await this.getParametro('compuerta_integral_mensual', ciudadId, ejecutivo.perfilVenta);
      const puedePagar = cumplimientoIntegral >= compuertaIntegral;

      let comisionTotal = 0;
      const explicacionesPartiales = [];

      // Calculate commission for each budget type
      for (const [tipoId, pptoVal] of Object.entries(pptoByType)) {
        // Parse ID back to int if necessary
        const tId = tipoId === 'null' ? null : parseInt(tipoId);
        const typeObj = budgetTypes.find(bt => bt.id === tId);
        const typeName = typeObj ? typeObj.nombre : 'General';

        // Distribute sales proportionally to the budget type (or other logic if preferred)
        // Here we assume sales apply to the total budget and we use the type-specific parameters
        const proportion = pptoVal / totalPpto;
        const ventaProporcional = ventaRealTotal * proportion;
        const cumplimiento = pptoVal > 0 ? ventaProporcional / pptoVal : 0;

        // Get bands and parameters for THIS specific type
        const banda = await this.getBanda(cumplimiento, 'mensual', ciudadId, ejecutivo.perfilVenta, tId);
        if (!banda) continue;

        const tasaBaseAnual = await this.getParametro('tasa_base_comision', ciudadId, ejecutivo.perfilVenta, tId);
        const pesoMensual = await this.getParametro('bolsa_mensual', ciudadId, ejecutivo.perfilVenta, tId);
        const tasaBaseMensual = tasaBaseAnual * pesoMensual;
        
        const medio = await Medio.findByPk(medioId);
        const tasaFinal = tasaBaseMensual * banda.factorIndividual * (medio?.pesoEstrategico || 1);
        const comisionTipo = ventaProporcional * tasaFinal;
        
        comisionTotal += comisionTipo;
        explicacionesPartiales.push(`Tipo ${typeName}: $${comisionTipo.toLocaleString('es-CO')} (Tasa: ${(tasaFinal*100).toFixed(4)}%, Banda: ${banda.nombre})`);
      }

      // Save calculation
      await CalculoComision.create({
        ejecutivoId,
        ciudadId,
        medioId,
        anio,
        mes,
        tipo: 'mensual',
        presupuesto: totalPpto,
        ventaReal: ventaRealTotal,
        cumplimiento: totalPpto > 0 ? ventaRealTotal / totalPpto : 0,
        comision: comisionTotal,
        explicacion: `Cálculo Mensual:\n${explicacionesPartiales.join('\n')}\nCumplimiento Integral: ${(cumplimientoIntegral*100).toFixed(2)}% (${puedePagar ? 'PAGABLE' : 'BLOQUEADO'})`
      });

      return {
        success: true,
        message: 'Cálculo mensual realizado exitosamente',
        data: {
          comision: comisionTotal,
          puedePagar,
          explicacion: explicacionesPartiales.join('\n')
        }
      };

    } catch (error) {
      return {
        success: false,
        message: `Error en cálculo mensual: ${error.message}`,
        data: null
      };
    }
  }

  // Calcular comisión trimestral
  async calcularComisionTrimestral(ejecutivoId, ciudadId, medioId, anio, trimestre) {
    try {
      const mesInicio = (trimestre - 1) * 3 + 1;
      const mesFin = trimestre * 3;

      // Obtener ventas del trimestre
      const ventas = await Venta.findAll({
        where: {
          ejecutivoId,
          ciudadId,
          medioId,
          anio,
          trimestre,
          activo: true
        }
      });

      if (ventas.length === 0) {
        return {
          success: false,
          message: 'No se encontraron ventas para este trimestre',
          data: null
        };
      }

      const ventaRealTotal = ventas.reduce((sum, venta) => sum + (venta.ventaReal || 0), 0);
      
      // Obtener presupuestos del trimestre agrupados por tipo
      const presupuestos = await Presupuesto.findAll({
        where: {
          ejecutivoId,
          ciudadId,
          medioId,
          anio,
          activo: true,
          mes: { $gte: mesInicio, $lte: mesFin }
        }
      });
      
      const pptoByType = {};
      const budgetTypes = await DatabaseManager.TipoPresupuesto.findAll();
      presupuestos.forEach(p => {
        let typeId = p.tipoPresupuestoId;
        if (!typeId && p.tipo) {
          const matched = budgetTypes.find(bt => bt.nombre.toLowerCase() === p.tipo.toLowerCase());
          if (matched) typeId = matched.id;
        }
        if (!typeId) {
          const baseType = budgetTypes.find(bt => bt.nombre === 'Base');
          typeId = baseType ? baseType.id : null;
        }
        pptoByType[typeId] = (pptoByType[typeId] || 0) + (p.presupuesto || 0);
      });

      const presupuestoTotal = Object.values(pptoByType).reduce((sum, p) => sum + p, 0);
      const ejecutivo = await Ejecutivo.findByPk(ejecutivoId);

      if (presupuestoTotal <= 0) {
        return {
          success: false,
          message: 'No se encontraron presupuestos válidos para este trimestre',
          data: null
        };
      }

      // Check integral compliance and factor balance
      const cumplimientoIntegral = await this.calcularCumplimientoIntegral(ejecutivoId, anio, mesFin, 'trimestral');
      const compuertaIntegral = await this.getParametro('compuerta_integral_trimestral', ciudadId, ejecutivo.perfilVenta);
      const factorBalance = await this.getFactorBalance(ejecutivoId, anio, trimestre);
      const puedePagar = cumplimientoIntegral >= compuertaIntegral && factorBalance > 0;

      let comisionTotal = 0;
      const explicacionesPartiales = [];

      for (const [tipoId, pptoVal] of Object.entries(pptoByType)) {
        const tId = tipoId === 'null' ? null : parseInt(tipoId);
        const typeObj = budgetTypes.find(bt => bt.id === tId);
        const typeName = typeObj ? typeObj.nombre : 'General';

        const proportion = pptoVal / presupuestoTotal;
        const ventaProporcional = ventaRealTotal * proportion;
        const cumplimiento = pptoVal > 0 ? ventaProporcional / pptoVal : 0;

        const banda = await this.getBanda(cumplimiento, 'trimestral', ciudadId, ejecutivo.perfilVenta, tId);
        if (!banda) continue;

        const tasaBaseAnual = await this.getParametro('tasa_base_comision', ciudadId, ejecutivo.perfilVenta, tId);
        const pesoTrimestral = await this.getParametro('bolsa_trimestral', ciudadId, ejecutivo.perfilVenta, tId);
        const tasaBaseTrimestral = tasaBaseAnual * pesoTrimestral;
        
        const medio = await Medio.findByPk(medioId);
        const tasaFinal = tasaBaseTrimestral * banda.factorIntegral * (medio?.pesoEstrategico || 1) * factorBalance;
        const comisionTipo = ventaProporcional * tasaFinal;
        
        comisionTotal += comisionTipo;
        explicacionesPartiales.push(`Tipo ${typeName}: $${comisionTipo.toLocaleString('es-CO')} (Tasa: ${(tasaFinal*100).toFixed(4)}%, Banda: ${banda.nombre})`);
      }

      // Guardar cálculo
      await CalculoComision.create({
        ejecutivoId,
        ciudadId,
        medioId,
        anio,
        mes: mesFin,
        trimestre,
        tipoBanda: 'trimestral',
        presupuesto: presupuestoTotal,
        ventaReal: ventaRealTotal,
        comisionCalculada: comisionTotal,
        puedePagar,
        explicacion: explicacionesPartiales.join(' | '),
        activo: true
      });

      return {
        success: true,
        message: 'Comisión trimestral calculada con éxito',
        data: {
          comision: comisionTotal,
          explicacion: explicacionesPartiales.join(' | '),
          puedePagar
        }
      };
    } catch (error) {
      console.error('Error al calcular comisión trimestral:', error);
      return {
        success: false,
        message: 'Error en el cálculo: ' + error.message,
        data: null
      };
    }
  }

  // Construir explicación detallada mensual
  construirExplicacionMensual(data) {
    return `
CÁLCULO DE COMISIÓN MENSUAL - Medio ID: ${data.medioId} - ${data.mes}/${data.anio}

🔍 1. TUS RESULTADOS BÁSICOS:
   • Oportunidad (Ppto): $${data.presupuesto.toLocaleString('es-CO')}
   • Logro Real (Venta): $${data.ventaReal.toLocaleString('es-CO')}
   • Cumplimiento Mínimo: ${(data.cumplimiento * 100).toFixed(2)}%

🏆 2. TRAMO DE CUMPLIMIENTO (BANDA):
   Caiste en la "${data.banda}":
   • Multiplicador por desempeño individual: ${data.factorIndividual}
   • Multiplicador grupal integral: ${data.factorIntegral}

🧮 3. LIQUIDACIÓN DE LA TASA ABSOLUTA:
   • Tasa Variable Mensual Base: ${(data.tasaBase * 100).toFixed(4)}%
   (Esto incluye el peso estratégico del producto que empujas)
   ➤ Tasa Final Aplicada: ${(data.tasaFinal * 100).toFixed(4)}%

🚧 4. REGLA INTEGRAL - TODOS DEBEMOS CRECER:
   • Tu resultado ponderado total este mes fue: ${(data.cumplimientoIntegral * 100).toFixed(2)}%
   👉 ${data.puedePagar 
        ? '¡Superaste el umbral mensual permitido! APROBADO ✅' 
        : 'Cuidado: Tu desarrollo integral en todo tu grupo de portafolio/presupuestos fue MENOR al límite de la compañía. TE QUEDAS BLOQUEADO. ❌'}

💰 5. TU COMISIÓN RESULTANTE DEL ESFUERZO:
   ➤ COMISIÓN INYECTADA A BOLSILLO DEL MES: $${data.comision.toLocaleString('es-CO')}
    `.trim();
  }

  // Construir explicación detallada trimestral
  construirExplicacionTrimestral(data) {
    return `
CÁLCULO DE COMISIÓN TRIMESTRAL - Medio ID: ${data.medioId} - T${data.trimestre}/${data.anio}

🔍 1. TUS RESULTADOS TRIMESTRALES:
   • Oportunidad (Ppto acumulado): $${data.presupuesto.toLocaleString('es-CO')}
   • Logro Real (Venta acumulada): $${data.ventaReal.toLocaleString('es-CO')}
   • Cumplimiento del Medio: ${(data.cumplimiento * 100).toFixed(2)}%

🏆 2. TRAMO DE CUMPLIMIENTO (BANDA TRIMESTRAL):
   Lograste la "${data.banda}":
   • Multiplicador por desempeño individual Trimestral: ${data.factorIndividual}
   • Multiplicador grupal integral Trimestral: ${data.factorIntegral}

⚖️ 3. FACTOR DE EQUILIBRIO(BALANCE):
   • Tu factor de balance es: ${data.factorBalance}
   • Escenario detonado: "${this.getCondicionBalance(data.factorBalance)}"

🧮 4. LIQUIDACIÓN DE LA TASA TRIMESTRAL:
   • Tasa Trimestral Exigida Base: ${(data.tasaBase * 100).toFixed(4)}%
   ➤ Tasa Final Aplicada al Periodo: ${(data.tasaFinal * 100).toFixed(4)}%

🚧 5. REGLA INTEGRAL ACUMULADA:
   • Tu resultado ponderado global Trimestral fue: ${(data.cumplimientoIntegral * 100).toFixed(2)}%
   👉 ${data.puedePagar 
        ? '¡Cumpliste tanto el mínimo integral como el balance por productos! APROBADO ✅' 
        : 'Bloqueado por factor de balance o límite Trimestral ❌'}

💰 6. CIERRE DE BOLSA TRIMESTRAL:
   ➤ COMISION OBTENIDA: $${data.comision.toLocaleString('es-CO')}
    `.trim();
  }

  // Obtener condición de balance
  getCondicionBalance(factorBalance) {
    if (factorBalance === 0) return 'Algún medio < 70%';
    if (factorBalance === 0.7) return 'Algún medio < 80%';
    if (factorBalance === 1) return 'Todos >= 85%';
    if (factorBalance === 1.1) return 'Todos >= 95%';
    if (factorBalance === 1.25) return 'Todos >= 100%';
    return 'Condición no definida';
  }

  // Calcular proyecciones
  async calcularProyecciones(ejecutivoId, ciudadId, medioId, anio, mes) {
    try {
      const proyecciones = [];
      
      // Proyección para los próximos 3 meses
      for (let i = 0; i < 3; i++) {
        const mesProyeccion = mes + i;
        const anioProyeccion = mesProyeccion > 12 ? anio + 1 : anio;
        const mesActual = mesProyeccion > 12 ? mesProyeccion - 12 : mesProyeccion;

        // Obtener presupuesto del mes
        const presupuesto = await Presupuesto.findOne({
          where: {
            ejecutivoId,
            ciudadId,
            medioId,
            anio: anioProyeccion,
            mes: mesActual,
            activo: true
          }
        });

        if (!presupuesto) continue;

        // Obtener ventas reales hasta ahora
        const ventasReales = await Venta.findAll({
          where: {
            ejecutivoId,
            ciudadId,
            medioId,
            anio: anioProyeccion,
            mes: { $lte: mesActual },
            activo: true
          }
        });

        // Calcular promedio de ventas y proyectar
        const promedioVentas = ventasReales.length > 0 
          ? ventasReales.reduce((sum, v) => sum + (v.ventaReal || 0), 0) / ventasReales.length 
          : 0;

        const proyeccion = presupuesto.presupuesto * 1.1; // Suponer 10% sobre presupuesto
        
        proyecciones.push({
          mes: mesActual,
          anio: anioProyeccion,
          presupuesto: presupuesto.presupuesto,
          proyeccion,
          cumplimientoEstimado: proyeccion / presupuesto.presupuesto,
          comisionEstimada: proyeccion * 0.004 * 0.34 // Estimación simple
        });
      }

      return {
        success: true,
        message: 'Proyecciones calculadas exitosamente',
        data: proyecciones
      };

    } catch (error) {
      return {
        success: false,
        message: `Error en proyecciones: ${error.message}`,
        data: null
      };
    }
  }
}

export default CommissionCalculator;